import { useState } from "react";

import { Avatar } from "../../ui/avatar";
import { Button } from "../../ui/button";
import { EmptyState } from "../../ui/empty-state";
import { TextField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { useAnalyticsCustomers } from "../queries";

// The list of the customers that the SERVER allows this staff member to open (a concierge sees only the customers they cover), with a search field
// and "Show more". The landing page `/analytics` and the "Switch customer" dialog both show it, so a supervisor or an administrator can reach
// the 21st customer without opening an unrelated one first (every opening is written to the audit trail). The list filters nothing itself and never
// tells a customer outside the cover from one that does not exist. The search waits for the person to stop typing (the query is the field text,
// trimmed, sent when the person presses Search or Enter). A row opens nothing: the caller does, when a row is chosen.

const REASON_WORDS = {
  SUPERVISOR: "Any customer",
  ADMINISTRATOR: "Any customer",
  PRIMARY: "Your customer",
  BACKUP: "Backup cover",
  TEMPORARY: "Temporary cover",
} as const;

type Rows = ReturnType<typeof useAnalyticsCustomers>;
type Row = NonNullable<Rows["data"]>["pages"][number]["rows"][number];

function CustomerRow({
  row,
  page,
  onPick,
}: Readonly<{
  row: Row;
  page: boolean;
  onPick: (customerId: string, displayName: string) => void;
}>) {
  return (
    <li>
      <Button
        type="button"
        variant="ghost"
        onClick={() => onPick(row.customerId, row.displayName)}
        className={
          page
            ? "h-auto min-h-12 w-full justify-start gap-3 px-3 py-1 text-left compact:min-h-9"
            : "h-auto min-h-11 w-full justify-start gap-3 px-2 py-1 text-left compact:min-h-9"
        }
      >
        <Avatar
          id={row.customerId}
          name={row.displayName}
          {...(page ? {} : { size: "sm" as const })}
        />
        <span
          className={`min-w-0 flex-1 truncate font-serif font-medium ${page ? "text-lg" : "text-base"}`}
        >
          {row.displayName}
        </span>
        <span className="text-xs font-normal text-muted-foreground">
          {REASON_WORDS[row.accessReason]}
        </span>
      </Button>
    </li>
  );
}

/** What the list shows for the state of the read: the error, the wait, the empty answer or the rows. */
function CustomerRows({
  list,
  query,
  page,
  onPick,
}: Readonly<{
  list: Rows;
  query: string;
  page: boolean;
  onPick: (customerId: string, displayName: string) => void;
}>) {
  const rows = list.data?.pages.flatMap((entry) => entry.rows) ?? [];
  if (list.isError) {
    return (
      <Notice tone="danger" role="alert">
        The customers could not be loaded.{" "}
        <Button type="button" variant="link" onClick={() => list.refetch()}>
          Try again
        </Button>
      </Notice>
    );
  }
  if (list.isPending) {
    return (
      <p role="status" className="m-0 text-sm text-muted-foreground">
        Loading customers…
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No customer found"
        description={query === "" ? "You have no customer to open yet." : "Try another name."}
      />
    );
  }
  return (
    <ul className={`m-0 grid list-none gap-1 p-0 ${page ? "" : "max-h-80 overflow-y-auto"}`}>
      {rows.map((row) => (
        <CustomerRow key={row.customerId} row={row} page={page} onPick={onPick} />
      ))}
    </ul>
  );
}

export function CustomerList({
  onPick,
  size,
  idPrefix,
}: Readonly<{
  onPick: (customerId: string, displayName: string) => void;
  /** `page`: larger rows for a page. `dialog`: compact rows inside a dialog. */
  size: "page" | "dialog";
  /** Makes the id of the search field unique when the list is shown twice. */
  idPrefix: string;
}>) {
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const list = useAnalyticsCustomers(query, true);
  return (
    <div className="grid gap-3">
      <form
        className="grid gap-2"
        aria-label="Search customers"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(text.trim());
        }}
      >
        <TextField
          id={`${idPrefix}-query`}
          type="search"
          label="Search by name"
          value={text}
          onChange={setText}
          maxLength={64}
        />
        <div>
          <Button type="submit" variant="outline" size="sm">
            Search
          </Button>
        </div>
      </form>
      <CustomerRows list={list} query={query} page={size === "page"} onPick={onPick} />
      {list.hasNextPage ? (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={list.isFetchingNextPage}
            onClick={() => list.fetchNextPage()}
          >
            Show more
          </Button>
        </div>
      ) : null}
    </div>
  );
}
